import type { useTranslation } from 'react-i18next'

import type { ToolActivityLabels } from '../../domain/toolActivity'

type Translate = ReturnType<typeof useTranslation>['t']

/**
 * ツールの呼び出しの「何をしているか」の一行の文言。ツールの行と束の見出しで同じ言い方にする。
 * t は部品の useTranslation から受け、言語を切り替えたときに部品ごと描き直されるようにする
 */
export const toolActivityLabelsOf = (t: Translate): ToolActivityLabels => ({
  salescoreMcp: {
    list_models: t(`利用できるデータの一覧を確認`),
    describe_model: t(`データの項目を確認`),
    profile_property: t(`データの値の分布を確認`),
    run_aggregate: t(`その場で集計を実行`),
    preview_drilldown: t(`ドリルダウンの実データを数行確認`),
    list_users: t(`ユーザーを検索`),
    search_views: t(`既存の KPI・ダッシュボード・シートを検索`),
    find_similar_kpis: t(`似た KPI を検索`),
    get_kpi: t(`KPI の設定を読み取り`),
    create_kpi: t(`KPI を作成`),
    update_kpi: t(`KPI の設定を更新`),
    create_dashboard: t(`ダッシュボードを作成`),
    get_dashboard: t(`ダッシュボードの構成を読み取り`),
    update_dashboard: t(`ダッシュボードを更新`),
    duplicate_dashboard: t(`ダッシュボードを複製`),
    create_sheet: t(`シートを作成`),
    get_sheet: t(`シートの設定を読み取り`),
    update_sheet: t(`シートの設定を更新`),
    upsert_records: t(`カスタムオブジェクトのレコードを書き込み`),
    archive_view: t(`ビューをゴミ箱に移動`),
    restore_view: t(`ビューをゴミ箱から戻す`),
    list_custom_views: t(`カスタムビューの一覧を確認`),
    create_custom_view: t(`カスタムビューを作成`),
    propose_custom_view_sql: t(`カスタムビューの SQL を検証`),
    create_custom_view_sql: t(`カスタムビューを作成`),
    create_custom_object: t(`カスタムオブジェクトを作成`),
    list_dimension_groups: t(`軸グループの一覧を確認`),
    create_dimension_group: t(`軸グループを作成`),
    update_dimension_group: t(`軸グループを更新`),
    list_user_groups: t(`ユーザーグループの一覧を確認`),
    upsert_user_groups: t(`ユーザーグループを更新`),
    assign_users_to_user_group: t(`ユーザーグループの所属ユーザーを変更`),
    list_goal_configs: t(`目標設定の一覧を確認`),
    create_goal_config: t(`目標設定を作成`),
    upsert_goals: t(`目標値を書き込み`),
    delete_goal_config: t(`目標設定を削除`),
  },
  salescoreMcpWithSubject: {
    describe_model: (subject) => t(`データ「{{subject}}」の項目を確認`, { subject }),
    profile_property: (subject) => t(`データ「{{subject}}」の値の分布を確認`, { subject }),
    create_kpi: (subject) => t(`KPI「{{subject}}」を作成`, { subject }),
    create_dashboard: (subject) => t(`ダッシュボード「{{subject}}」を作成`, { subject }),
    duplicate_dashboard: (subject) => t(`ダッシュボードを複製して「{{subject}}」を作成`, { subject }),
    create_sheet: (subject) => t(`シート「{{subject}}」を作成`, { subject }),
    create_custom_view: (subject) => t(`カスタムビュー「{{subject}}」を作成`, { subject }),
    create_custom_view_sql: (subject) => t(`カスタムビュー「{{subject}}」を作成`, { subject }),
    create_custom_object: (subject) => t(`カスタムオブジェクト「{{subject}}」を作成`, { subject }),
    create_dimension_group: (subject) => t(`軸グループ「{{subject}}」を作成`, { subject }),
    create_goal_config: (subject) => t(`目標設定「{{subject}}」を作成`, { subject }),
  },
  otherMcp: (tool) => (tool === null ? t(`処理を実行`) : t(`処理を実行（{{tool}}）`, { tool })),
})
